import { SxProps, TextField, Theme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

const styles = {
  maxWidth: 96,
  '& .MuiInputBase-input': {
    textAlign: 'center',
    py: 1,
  },
};

type PositionInputProps = {
  value?: number | null;
  onCommit: (value: number | null) => Promise<void>;
  onValueChange?: (value: number | null) => void;
  preserveDirtyValue?: boolean;
  sx?: SxProps<Theme>;
};

const PositionInput = ({ value, onCommit, onValueChange, preserveDirtyValue = false, sx }: PositionInputProps) => {
  const [inputValue, setInputValue] = useState(value?.toString() || '');
  const isDirtyRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (preserveDirtyValue && isDirtyRef.current) return;

    setInputValue(value?.toString() || '');
  }, [preserveDirtyValue, value]);

  const resetValue = () => {
    isDirtyRef.current = false;
    setInputValue(value?.toString() || '');
    onValueChange?.(value ?? null);
  };

  const handleCommit = async () => {
    if (isSubmitting) return;

    const rawValue = inputValue.trim();
    const nextValue = rawValue === '' ? null : Number(rawValue);

    if (rawValue !== '' && Number.isNaN(nextValue)) {
      resetValue();
      return;
    }

    if (nextValue === (value ?? null)) {
      isDirtyRef.current = false;
      return;
    }

    try {
      setIsSubmitting(true);
      await onCommit(nextValue);
      isDirtyRef.current = false;
      setInputValue(nextValue?.toString() || '');
    } catch {
      resetValue();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;

    if (nextValue === '' || /^\d+$/.test(nextValue)) {
      isDirtyRef.current = true;
      setInputValue(nextValue);
      onValueChange?.(nextValue === '' ? null : Number(nextValue));
    }
  };

  const stopPropagation = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <TextField
      value={inputValue}
      onChange={handleChange}
      onBlur={handleCommit}
      onClick={stopPropagation}
      onFocus={stopPropagation}
      onMouseDown={stopPropagation}
      onKeyDown={(event) => {
        event.stopPropagation();

        if (event.key === 'Enter') {
          event.preventDefault();
          void handleCommit();
        }

        if (event.key === 'Escape') {
          resetValue();
        }
      }}
      disabled={isSubmitting}
      size='small'
      sx={sx ?? styles}
      slotProps={{
        htmlInput: {
          inputMode: 'numeric',
        },
      }}
    />
  );
};

export default PositionInput;
